/* ══════════════════════════════════════════════════════════════
   Backtest Simulator Service — Frontend wrapper for backtest
   Provides simulation execution, parameter management,
   and result formatting for the backtest page
   ══════════════════════════════════════════════════════════════ */

import { apiFetch } from './api-client';

export interface BacktestResult {
    trades: Array<{
        entryDate: string; exitDate: string; strategy: string; regime: string;
        entryPrice: number; exitPrice: number; pnl: number; pnlPct: number;
        holdingDays: number; costs: number;
    }>;
    dailyReturns: number[];
    equityCurve: number[];
    metrics: Record<string, number>;
    regimeBreakdown: Record<string, { trades: number; winRate: number; avgPnl: number }>;
    strategyBreakdown: Record<string, { trades: number; winRate: number; avgPnl: number }>;
    config: { initialCapital: number; startDate: string; endDate: string; totalDays: number };
}

export interface SimulationParams {
    strategy: string;
    instrument: string;
    startDate: string;
    endDate: string;
    initialCapital: number;
    positionSizePct: number;
    stopLossPct: number;
    targetPct: number;
    trailingStopPct?: number;
}

export interface SimulationStatus {
    state: 'idle' | 'running' | 'complete' | 'error';
    progress: number;          // 0-100
    message: string;
    result?: BacktestResult;
    error?: string;
}

const DEFAULT_PARAMS: SimulationParams = {
    strategy: 'momentum_long',
    instrument: 'NIFTY50',
    startDate: '2021-01-01',
    endDate: '2026-01-01',
    initialCapital: 1000000,
    positionSizePct: 10,
    stopLossPct: 2,
    targetPct: 4,
};

/** Get default simulation parameters */
export function getDefaultParams(): SimulationParams {
    return { ...DEFAULT_PARAMS };
}

/** Run backtest simulation via API */
export async function runBacktestSimulation(params: SimulationParams): Promise<SimulationStatus> {
    try {
        const result = await apiFetch<BacktestResult>('/ml/backtest', {
            cacheTTL: 0, // Never cache simulation results
            params: {
                strategy: params.strategy,
                instrument: params.instrument,
                startDate: params.startDate,
                endDate: params.endDate,
                capital: String(params.initialCapital),
                positionSize: String(params.positionSizePct),
                stopLoss: String(params.stopLossPct),
                target: String(params.targetPct),
                trailingStop: String(params.trailingStopPct || 0),
            },
        });

        return {
            state: 'complete',
            progress: 100,
            message: 'Simulation complete',
            result: result.data,
        };
    } catch (err) {
        console.warn('Backtest simulation failed:', err);
        return {
            state: 'error',
            progress: 0,
            message: 'Simulation failed',
            error: err instanceof Error ? err.message : 'Unknown error',
        };
    }
}

/** Run client-side backtest using the local backtest module */
export async function runLocalBacktest(params: SimulationParams): Promise<SimulationStatus> {
    void params;
    return {
        state: 'error',
        progress: 0,
        message: 'Local simulation unavailable',
        error: 'Demo local backtest was removed. Use the live API-backed simulation instead.',
    };
}

/** Get available strategies for backtesting */
export function getAvailableStrategies(): Array<{ id: string; name: string; description: string }> {
    return [
        { id: 'momentum_long', name: 'Momentum Long', description: 'Buy on strong upward momentum, exit on momentum fade' },
        { id: 'momentum_short', name: 'Momentum Short', description: 'Short on strong downward momentum' },
        { id: 'mean_reversion', name: 'Mean Reversion', description: 'Buy oversold, sell overbought using RSI/Bollinger' },
        { id: 'straddle_strangle', name: 'Straddle/Strangle', description: 'Options selling strategy for high VIX regime' },
        { id: 'iron_condor', name: 'Iron Condor', description: 'Range-bound options strategy for sideways markets' },
        { id: 'fii_following', name: 'FII Following', description: 'Follow FII buying/selling patterns' },
    ];
}
